import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { Chart } from 'angular-highcharts';
import { Subscription, map, of, takeWhile } from 'rxjs';
import * as ElementModels from '../../../models/report-view.models';
import * as Models from '../../../models/models-index';
import * as Highcharts from 'highcharts';
import { Enums } from '../../../enums/enums';
import * as SharedServices from '../../../services/services-index';
import { ChartService } from '../../chart/chart.service';
import { lineChartWithForecastPlaceholder } from '../../../constants/highcharts-placeholders';

type HighchartsRangePoint = Highcharts.Point & { low: number | null; high: number | null };

@Component({
    selector: 'line-chart-with-forecast',
    templateUrl: './line-chart-with-forecast.component.html',
    styleUrls: ['./line-chart-with-forecast.component.scss'],
    standalone: false
})
export class LineChartWithForecastComponent implements Models.ElementComponent, OnInit, OnDestroy {
  @Input() set dataSets(value: Models.DataSet[]) {
    if (!!value && value.length > 0) {
      this._dataSets = value;
      // For backward compatibility: If only one dataset, treat it as forecast
      // If two datasets: First is forecast, second is actual
      if (value.length === 1) {
        this.processForecastData(value[0]);
      } else if (value.length >= 2) {
        this.processDualDataSets(value[0], value[1]); // Process both forecast and actual
      }
      this.isDataSetLoaded$ = of(true);
    } else {
      this.isDataSetLoaded$ = of(false);
    }
  }

  isDataSetLoaded$ = of(false);

  @Input() settings: ElementModels.ElementSettings;
  @Input() selectedMetrics: string[];

  _dataSets: Models.DataSet[] = [];

  subscriptions: Subscription[] = [];
  currentChart: Chart;
  chartTitle: string = '';
  locale: string;
  componentActive: boolean = true;
  enums = Enums;
  labelTranslator = (val) => this.sharedTranslationService.getLabelTranslation(val, this.locale);

  placeholderChart: Chart;

  constructor(
    private sharedTranslationService: SharedServices.SharedTranslationService,
    private localeService: SharedServices.LocaleService,
    private chartService: ChartService
  ) { }

  ngOnInit() {
    this.initializePlaceholder();
  }

  ngOnDestroy(): void {
    this.componentActive = false;
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  // Keep the original method name for backward compatibility
  // but delegate to processForecastData to maintain the single-dataset behavior
  private processData(dataSet: Models.DataSet): void {
    // For backward compatibility, treat a single dataset as forecast data
    this.processForecastData(dataSet);
  }

  private processForecastData(dataSet: Models.DataSet): void {
    this.subscriptions.push(
      this.localeService.locale$.subscribe(loc => this.locale = loc)
    );

    const metricIndexMapping = dataSet.columns.reduce((map, col, index) => {
      map[col.name] = { ...col, index };
      return map;
    }, {} as Record<string, typeof dataSet.columns[0] & { index: number }>);

    let forecastedSaleData = [];
    let rangeData = [];
    
    const currentDate = new Date();
    // Set to midnight for proper comparison
    const currentDateTimestamp = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate()).getTime();
        
    // Get metric names from settings or use defaults
    const projectedSalesMetricName = this.getProjectedSalesMetricName();
    const forecastedSalesMinMetricName = this.getForecastedSalesMinMetricName();
    const forecastedSalesMaxMetricName = this.getForecastedSalesMaxMetricName();
    
    // Show as forecasted data only
    dataSet.rows.forEach(row => {
      const date = new Date(row[metricIndexMapping[this.settings.dimensionName].index].label).setHours(0, 0, 0, 0);
      const projectedSales = row[metricIndexMapping[projectedSalesMetricName]?.index]?.value;
      const forecastedSalesMin = row[metricIndexMapping[forecastedSalesMinMetricName]?.index]?.value;
      const forecastedSalesMax = row[metricIndexMapping[forecastedSalesMaxMetricName]?.index]?.value;
      
      forecastedSaleData.push([date, projectedSales]);
      
      // Only show range for current date and future dates
      if (date >= currentDateTimestamp) {
        rangeData.push([
          date,
          forecastedSalesMin ?? null,
          forecastedSalesMax ?? null
        ]);
      } else {
        // For past dates, push nulls for the range so nothing is displayed
        rangeData.push([
          date,
          null,
          null
        ]);
      }
    });

    const isCurrentMonth = true; // Assume forecast is for current month
    this.currentChart = new Chart(this.generateForecastOnlyChart(forecastedSaleData, rangeData, isCurrentMonth));

    this.chartService.reflowChart$.pipe(
      takeWhile(() => this.componentActive),
      map(() => {
        setTimeout(() => {
          this.currentChart.ref.reflow();
        }, 200);
      })
    ).subscribe();
  }

  // New method for dual datasets - processes separate forecast and actual datasets
  private processDualDataSets(forecastDataSet: Models.DataSet, actualDataSet: Models.DataSet): void {
    this.subscriptions.push(
      this.localeService.locale$.subscribe(loc => this.locale = loc)
    );

    // console.log('forecastDataSet', forecastDataSet);
    // console.log('actualDataSet', actualDataSet);

    // Process forecast data
    const forecastMetricMapping = forecastDataSet.columns.reduce((map, col, index) => {
      map[col.name] = { ...col, index };
      return map;
    }, {} as Record<string, typeof forecastDataSet.columns[0] & { index: number }>);

    // Process actual data
    const actualMetricMapping = actualDataSet.columns.reduce((map, col, index) => {
      map[col.name] = { ...col, index };
      return map;
    }, {} as Record<string, typeof actualDataSet.columns[0] & { index: number }>);

    let forecastedSaleData = [];
    let rangeData = [];
    let actualSaleData = [];

    const currentDate = new Date();
    let mostRecentActualDate = null;

    // Get metric names from settings or use defaults
    const projectedSalesMetricName = this.getProjectedSalesMetricName();
    const forecastedSalesMinMetricName = this.getForecastedSalesMinMetricName();
    const forecastedSalesMaxMetricName = this.getForecastedSalesMaxMetricName();
    const totalSalesMetricName = this.getTotalSalesMetricName();

    // Set to midnight for proper comparison
    const todayDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate()).getTime();
    
    // Extract forecast data
    forecastDataSet.rows.forEach(row => {
      const date = new Date(row[forecastMetricMapping[this.settings.dimensionName].index].label).setHours(0, 0, 0, 0);
      const dateObj = new Date(date);
      const projectedSales = row[forecastMetricMapping[projectedSalesMetricName]?.index]?.value;
      const forecastedSalesMin = row[forecastMetricMapping[forecastedSalesMinMetricName]?.index]?.value;
      const forecastedSalesMax = row[forecastMetricMapping[forecastedSalesMaxMetricName]?.index]?.value;
      
      forecastedSaleData.push([date, projectedSales]);
      
      rangeData.push([
        date,
        date >= todayDate ? forecastedSalesMin ?? null : null,
        date >= todayDate ? forecastedSalesMax ?? null : null
      ]);
    });

    // Extract actual data
    actualDataSet.rows.forEach(row => {
      const dateRowValue = row[actualMetricMapping[this.settings.dimensionName].index];
      const date = new Date(dateRowValue.value).setHours(0, 0, 0, 0);
      const totalSales = row[actualMetricMapping[totalSalesMetricName]?.index]?.value;
      
      actualSaleData.push([date, totalSales]);
      
      // Keep track of most recent actual data date
      if (!mostRecentActualDate || date > mostRecentActualDate) {
        mostRecentActualDate = date;
      }
    });

    const isCurrentMonth = this.isCurrentMonth(forecastedSaleData);
    this.currentChart = new Chart(this.generateDualChart(actualSaleData, forecastedSaleData, rangeData, mostRecentActualDate, isCurrentMonth));

    this.chartService.reflowChart$.pipe(
      takeWhile(() => this.componentActive),
      map(() => {
        setTimeout(() => {
          this.currentChart.ref.reflow();
        }, 200);
      })
    ).subscribe();
  }

  private isCurrentMonth(data: any[]): boolean {
    if (!data || data.length === 0) return false;
    
    const currentDate = new Date();
    const sampleDate = new Date(data[0][0]);
    return sampleDate.getMonth() === currentDate.getMonth() && 
           sampleDate.getFullYear() === currentDate.getFullYear();
  }

  private calculateDateRange(actualSaleData, forecastedSaleData) {
    const allData = [...(actualSaleData || []), ...(forecastedSaleData || [])];
    const dates = allData.map(data => data[0]);
    const minDate = Math.min(...dates);
    const maxDate = Math.max(...dates);
    return { minDate, maxDate };
  }

  private generateChart(actualSaleData, forecastedSaleData, rangeData, mostRecentSaleDay: number, isCurrentMonth: boolean): any {
    // In the new implementation, we'd delegate to one of our new chart generators
    // depending on whether we have actual sales data or not
    if (!actualSaleData || actualSaleData.length === 0) {
      return this.generateForecastOnlyChart(forecastedSaleData, rangeData, isCurrentMonth);
    } else {
      return this.generateDualChart(actualSaleData, forecastedSaleData, rangeData, mostRecentSaleDay, isCurrentMonth);
    }
  }

  private generateForecastOnlyChart(forecastedSaleData, rangeData, isCurrentMonth: boolean): any {
    Highcharts.setOptions({
      colors: [this.enums.chartColors.color1, this.enums.chartColors.color2, '#000000'],
      lang: {
        decimalPoint: this.locale != 'en' ? ',' : '.',
        thousandsSep: this.locale != 'en' ? ' ' : ','
      }
    });

    // Get metric name for display
    const projectedSalesMetricName = this.getProjectedSalesMetricName();
    // For displaying in the chart, use the metric name or default to 'Projected Sales'
    const projectedSalesLabel = this.sharedTranslationService.getLabelTranslation(projectedSalesMetricName) != projectedSalesMetricName ?
                                this.sharedTranslationService.getLabelTranslation(projectedSalesMetricName) :
                                this.sharedTranslationService.getLabelTranslation('Projected Sales');

    const seriesData = [
      {
        name: projectedSalesLabel,
        data: forecastedSaleData,
        showInLegend: true,
        zIndex: 1,
        type: 'line',
        color: 'black',
        dashStyle: 'ShortDot'
      },
      {
        name: this.sharedTranslationService.getLabelTranslation('Range'),
        data: rangeData,
        showInLegend: false,
        type: 'arearange',
        lineWidth: 0,
        linkedTo: ':previous',
        color: 'black',
        fillOpacity: 0.3,
        zIndex: 0,
        marker: {
          enabled: false
        }
      }
    ];

    return {
      xAxis: {
        type: 'datetime',
        dateTimeLabelFormats: {
          day: '%m/%d'
        }
      },
      yAxis: {
        title: {
          text: this.sharedTranslationService.getLabelTranslation('Sales Volume')
        },
        labels: {
          formatter: function () {
            const value = this.value;
            if (value >= 1000000) {
              return (value / 1000000).toFixed(1) + 'M';
            } else if (value >= 1000) {
              return (value / 1000).toFixed(1) + 'k';
            }
            return value.toString();
          }
        }
      },
      legend: {
        enabled: true
      },
      title: {
        text: ''
      },
      tooltip: {
        shadow: false,
        shared: true,
        borderWidth: 1,
        formatter: this.getForecastTooltipFormatter(),
        borderRadius: 0,
        valueSuffix: ''
      },
      credits: {
        enabled: false
      },
      exporting: {
        enabled: false
      },
      series: seriesData
    }
  }

  private generateDualChart(actualSaleData, forecastedSaleData, rangeData, mostRecentActualDate: number, isCurrentMonth: boolean): any {
    Highcharts.setOptions({
      colors: [this.enums.chartColors.color1, this.enums.chartColors.color2, '#000000'],
      lang: {
        decimalPoint: this.locale != 'en' ? ',' : '.',
        thousandsSep: this.locale != 'en' ? ' ' : ','
      }
    });

    const [forecastedDataSet, actualDataSet] = this._dataSets;

    // Get metric names for display
    const projectedSalesMetricName = this.getProjectedSalesMetricName();
    const totalSalesMetricName = this.getTotalSalesMetricName();
    // const projectedSalesColumnLabel = forecastedDataSet.columns.find(col => col.name === projectedSalesMetricName)?.displayName || projectedSalesMetricName;
    // const totalSalesColumnLabel = actualDataSet.columns.find(col => col.name === totalSalesMetricName)?.displayName || totalSalesMetricName;

    // For displaying in the chart, use the metric name or default to standard labels
    const actualSalesLabel = this.sharedTranslationService.getLabelTranslation(totalSalesMetricName) != totalSalesMetricName ?
                            this.sharedTranslationService.getLabelTranslation(totalSalesMetricName) :
                            this.sharedTranslationService.getLabelTranslation('Actual Sales');
    const projectedSalesLabel = this.sharedTranslationService.getLabelTranslation(projectedSalesMetricName) != projectedSalesMetricName ?
                                this.sharedTranslationService.getLabelTranslation(projectedSalesMetricName) :
                                this.sharedTranslationService.getLabelTranslation('Projected Sales');

    // console.log('actualSaleData', actualSaleData);

    const seriesData = [
      {
        name: actualSalesLabel,
        data: actualSaleData,
        showInLegend: true,
        zIndex: 2,
        type: 'line',
        color: 'black',
        dashStyle: 'Solid'
      },
      {
        name: projectedSalesLabel,
        data: forecastedSaleData,
        showInLegend: true,
        zIndex: 1,
        type: 'line',
        color: this.enums.chartColors.color1,
        dashStyle: 'ShortDot'
      },
      {
        name: this.sharedTranslationService.getLabelTranslation('Range'),
        data: rangeData,
        showInLegend: false,
        type: 'arearange',
        lineWidth: 0,
        linkedTo: ':previous',
        color: this.enums.chartColors.color1,
        fillOpacity: 0.3,
        zIndex: 0,
        marker: {
          enabled: false
        }
      }
    ];

    return {
      xAxis: {
        type: 'datetime',
        dateTimeLabelFormats: {
          day: '%m/%d'
        }
      },
      yAxis: {
        title: {
          text: this.sharedTranslationService.getLabelTranslation('Sales Volume')
        },
        labels: {
          formatter: function () {
            const value = this.value;
            if (value >= 1000000) {
              return (value / 1000000).toFixed(1) + 'M';
            } else if (value >= 1000) {
              return (value / 1000).toFixed(1) + 'k';
            }
            return value.toString();
          }
        }
      },
      legend: {
        enabled: true
      },
      title: {
        text: ''
      },
      tooltip: {
        shadow: false,
        shared: true,
        borderWidth: 1,
        formatter: this.getDualDataTooltipFormatter(mostRecentActualDate),
        borderRadius: 0,
        valueSuffix: ''
      },
      credits: {
        enabled: false
      },
      exporting: {
        enabled: false
      },
      series: seriesData
    }
  }

  private getTooltipFormatter(mostRecentSaleDay: number, isCurrentMonth: boolean): Highcharts.TooltipFormatterCallbackFunction {
    // For backward compatibility, delegate to one of our new formatters
    return this.getDualDataTooltipFormatter(mostRecentSaleDay);
  }

  private getForecastTooltipFormatter(): Highcharts.TooltipFormatterCallbackFunction {
    const locale = this.locale; // Capture locale for use inside formatter
    
    // Get translated labels based on configured metric names
    const projectedMetricName = this.getProjectedSalesMetricName();
    const valueLabel = this.sharedTranslationService.getLabelTranslation('Value');
    const lowLabel = this.sharedTranslationService.getLabelTranslation('Low');
    const highLabel = this.sharedTranslationService.getLabelTranslation('High');
    
    return function () {
      const date = new Date(this.x).toLocaleDateString(locale, { month: '2-digit', day: '2-digit' });
      let result = `<b>${date} - ${this.points[0].series.name}</b>`;
      
      this.points.forEach(point => {
        if (point.series.type !== 'arearange') {
          result += `<br/><span style="color:${point.color}">\u25CF </span>${valueLabel}: ${point.y.toLocaleString()}`;
        } else {
          const rangePoint = point.point as HighchartsRangePoint;
          if (rangePoint.low !== null && rangePoint.high !== null) {
            result += `<br/><span style="color:${point.color}">\u25CF </span>${lowLabel}: ${rangePoint.low.toLocaleString()}`;
            result += `<br/><span style="color:${point.color}">\u25CF </span>${highLabel}: ${rangePoint.high.toLocaleString()}`;
          }
        }
      });
      
      return result;
    };
  }

  private getDualDataTooltipFormatter(mostRecentActualDate: number): Highcharts.TooltipFormatterCallbackFunction {
    const locale = this.locale; // Capture locale for use inside formatter
    
    // Get metric names for display from configuration or use defaults
    const projectedSalesMetricName = this.getProjectedSalesMetricName();
    const totalSalesMetricName = this.getTotalSalesMetricName();
    
    // Get translated labels based on configured metric names
    const actualSalesLabel = this.sharedTranslationService.getLabelTranslation(totalSalesMetricName) != totalSalesMetricName ?
                            this.sharedTranslationService.getLabelTranslation(totalSalesMetricName) : 
                            this.sharedTranslationService.getLabelTranslation('Actual Sales');
    const projectedSalesLabel = this.sharedTranslationService.getLabelTranslation(projectedSalesMetricName) != projectedSalesMetricName ?
                                this.sharedTranslationService.getLabelTranslation(projectedSalesMetricName) : 
                                this.sharedTranslationService.getLabelTranslation('Projected Sales');
    const actualLabel = this.sharedTranslationService.getLabelTranslation('Actual');
    const projectedLabel = this.sharedTranslationService.getLabelTranslation('Projected');
    const lowLabel = this.sharedTranslationService.getLabelTranslation('Low');
    const highLabel = this.sharedTranslationService.getLabelTranslation('High');
    
    return function () {
      const date = new Date(this.x).toLocaleDateString(locale, { month: '2-digit', day: '2-digit' });
      let result = `<b>${date}</b>`;
      
      // Group by series name for better organization
      const pointsByType = {};
      
      this.points.forEach(point => {
        const seriesName = point.series.name;
        if (!pointsByType[seriesName]) {
          pointsByType[seriesName] = [];
        }
        pointsByType[seriesName].push(point);
      });
      
      // Show actual sales first if available
      if (pointsByType[actualSalesLabel]) {
        const actualPoint = pointsByType[actualSalesLabel][0];
        result += `<br/><span style="color:${actualPoint.color}">\u25CF </span>${actualLabel}: ${actualPoint.y.toLocaleString()}`;
      }
      
      // Then show projected sales
      if (pointsByType[projectedSalesLabel]) {
        const projectedPoint = pointsByType[projectedSalesLabel][0];
        result += `<br/><span style="color:${projectedPoint.color}">\u25CF </span>${projectedLabel}: ${projectedPoint.y.toLocaleString()}`;
      }
      
      // Finally show range if available
      this.points.forEach(point => {
        const rangePoint = point.point as HighchartsRangePoint;
        if (point.series.type === 'arearange' && rangePoint.low !== null && rangePoint.high !== null) {
          result += `<br/><span style="color:${point.color}">\u25CF </span>${lowLabel}: ${rangePoint.low.toLocaleString()}`;
          result += `<br/><span style="color:${point.color}">\u25CF </span>${highLabel}: ${rangePoint.high.toLocaleString()}`;
        }
      });
      
      return result;
    };
  }

  private initializePlaceholder(): void {
    if (this.placeholderChart) {
      // exit if chart is already initialized
      return;
    }

    // without setTimeout the placeholder chart
    // won't respect the size of the parent container
    setTimeout(() => {
      this.placeholderChart = new Chart(lineChartWithForecastPlaceholder);
    }, 0);
  }

  // Helper methods to get metric names from settings or use default values for backwards compatibility
  private getProjectedSalesMetricName(): string {
    return this.settings?.metrics?.[0]?.name || 'ProjectedSales';
  }

  private getForecastedSalesMinMetricName(): string {
    return this.settings?.metrics?.[1]?.name || 'ForecastedSalesMin';
  }

  private getForecastedSalesMaxMetricName(): string {
    return this.settings?.metrics?.[2]?.name || 'ForecastedSalesMax';
  }

  private getTotalSalesMetricName(): string {
    return this.settings?.metrics?.[3]?.name || 'TotalSales';
  }
}
